import { ValidatedForm, useField, useIsSubmitting } from 'remix-validated-form';
import SVGIcon from '~/components/ui/Icons/SVGIcon';
import { Button } from '../buttons/Button';
import GenericModal from '../GenericModal';
import PhoneInputGroup from '../forms/PhoneInputGroup';
import { withYup } from '@remix-validated-form/with-yup';
import { phoneNumberSchema } from '~/validations/phoneNumberSchema';
import { useFetcher } from '@remix-run/react';
import { useTranslation } from 'react-i18next';
import type { UserData } from '~/types/userData';
import { useEffect, useRef, useState } from 'react';
import type { ApiErrorType } from '~/types/api';
import { ActivationDigits } from '../forms/ActivationDigits';
import { activationDigitsSchema } from '~/validations/activationDigitsSchema';

declare var bootstrap: any;

const phoneNumberValidator = withYup(phoneNumberSchema);
const phoneOTPValidator = withYup(activationDigitsSchema);

type TProps = {
  initialUserData: UserData;
  onPhoneNumberVerification: (user: UserData) => void;
};

type FetcherType = {
  user?: UserData | null;
  error?: ApiErrorType | null;
};

type StepType = 'sendOTP' | 'verifyOTP' | 'success';

export const PhoneVerificationModal = ({ initialUserData, onPhoneNumberVerification }: TProps) => {
  const [step, setStep] = useState<StepType>('sendOTP');
  const [user, setUser] = useState<UserData>(initialUserData);
  const sendOTPFetcher = useFetcher<FetcherType>();
  const verifyOTPFetcher = useFetcher<FetcherType>();
  const { t } = useTranslation();

  const sendOTPSubmitting = useIsSubmitting('send-otp-form');
  const verifyPhoneNumbereSubmitting = useIsSubmitting('verify-otp-form');

  const sendOTPForm = useRef<HTMLFormElement>(null);

  const { error } = useField('activationDigits', { formId: 'verify-otp-form' });

  const resendOTP = () => {
    sendOTPFetcher.submit(sendOTPForm.current);
  };

  const handleClose = () => {
    const modal = bootstrap.Modal.getOrCreateInstance('#phoneVerificationModal');
    modal?.hide();
    onPhoneNumberVerification(user);
  };

  useEffect(() => {
    if (sendOTPFetcher.state == 'loading' && !!sendOTPFetcher.data?.user) {
      setStep('verifyOTP');
      setUser(sendOTPFetcher.data.user);
    }
  }, [sendOTPFetcher]);

  useEffect(() => {
    if (verifyOTPFetcher.state == 'loading' && !!verifyOTPFetcher.data?.user) {
      setStep('success');
      setUser(verifyOTPFetcher.data.user);
    }
  }, [verifyOTPFetcher]);

  return (
    <GenericModal
      animate={false}
      extraClasses="modal-s"
      id="phoneVerificationModal"
      open={true}
      title={t('otp.modal.phoneVerification.title')}
    >
      {/* 'hidden with css so that the form is still in the dom and can be resubmitted programatically to resend the code on next step */}
      <ValidatedForm
        className={`modal-form-content ${step === 'sendOTP' ? '' : 'd-none'}`}
        id="send-otp-form"
        validator={phoneNumberValidator}
        method="post"
        action="/verify-phone-number"
        subaction="sendOTP"
        fetcher={sendOTPFetcher}
        formRef={sendOTPForm}
      >
        <div className="modal-body">
          <div className="d-flex justify-content-center mb-6">
            <img
              src="/media/phone-verification-illustration-1.svg"
              alt="Phone verification illustration"
            />
          </div>
          <p className="text-1 color-primary mb-6">{t('otp.modal.phoneVerification.description')}</p>
          <PhoneInputGroup
            forceStackedLayout={true}
            mobileNumberWithPrefix={user.phoneNumber}
          />
        </div>

        <div className="modal-footer justify-content-end">
          <Button
            size="xl"
            type="submit"
            disabled={sendOTPSubmitting}
          >
            <span>{t('global.btns.next')}</span>
            {sendOTPSubmitting ? (
              <div className="ms-2 fo-spinner" />
            ) : (
              <SVGIcon
                iconType="chevron-right"
                className="ms-2"
              />
            )}
          </Button>
        </div>
      </ValidatedForm>

      <ValidatedForm
        className={`modal-form-content ${step === 'verifyOTP' ? '' : 'd-none'}`}
        id="verify-otp-form"
        validator={phoneOTPValidator}
        method="post"
        action="/verify-phone-number"
        subaction="verifyOTP"
        fetcher={verifyOTPFetcher}
      >
        <div className="modal-body">
          <div className="d-flex justify-content-center mb-6">
            <img
              src="/media/phone-verification-illustration-1.svg"
              alt="Phone verification illustration"
            />
          </div>
          <p className="text-1 color-primary mb-6">
            {t('otp.modal.phoneVerification.otpText')} <b>{user.phoneNumber}</b>
          </p>
          <ActivationDigits
            extraClasses="mb-6"
            fetcher={verifyOTPFetcher}
            errorMessage={error ? t(error) : undefined}
          />
          <div>
            <span className="text-1 color-primary mb-6">{t('otp.modal.resend.text')}</span>{' '}
            <span
              onClick={resendOTP}
              role="button"
              className="text-1 color-grey-400 text-decoration-fo-underline"
            >
              {t('otp.modal.resend.link')}
            </span>
          </div>
        </div>
        <div className="modal-footer">
          <Button
            type="button"
            variant="primaryOutline"
            size="xl"
            disabled={verifyPhoneNumbereSubmitting}
            onClick={() => setStep('sendOTP')}
          >
            {!verifyPhoneNumbereSubmitting && (
              <SVGIcon
                iconType="chevron-left"
                className="me-2"
              />
            )}
            <span>{t('global.btns.back')}</span>
            {verifyPhoneNumbereSubmitting && <div className="fo-spinner" />}
          </Button>
        </div>
      </ValidatedForm>

      {step === 'success' && (
        <>
          <div className="modal-body">
            <img
              alt="Success"
              className="mb-3"
              src="/media-remix/icons/success-check.svg"
            />
            <div className="fo-display-4 -medium">{t('otp.modal.success.title')}</div>
          </div>
          <div className="modal-footer">
            <Button
              extraClasses="w-100"
              size="xl"
              onClick={handleClose}
            >
              <span>{t('global.btns.close')}</span>
            </Button>
          </div>
        </>
      )}
    </GenericModal>
  );
};
